/**
 * Data Review Page
 * Dedicated page for reviewing transactions needing human attention
 * 
 * Features:
 * - Shows only transactions needing review (low confidence, flagged, anomalies)
 * - Batch approve/reject
 * - Inline category editing with AI learning
 * - Anomaly detection results
 * - Progress tracking toward 100% approval
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { formatCurrency } from '../utils/financialFormat';
import {
  CheckCircle2, AlertTriangle, XCircle, Edit2, Check, X,
  ChevronLeft, ChevronRight, Filter, Download, RefreshCw,
  Sparkles, TrendingUp, Clock, AlertOctagon
} from 'lucide-react';
import { DataQualityReportPanel } from '../components/data-quality';

// Categories for dropdown
const CATEGORIES = [
  "Revenue", "Sales Revenue", "Other Income",
  "Payroll & Benefits", "Payroll", "Labor",
  "Rent & Occupancy", "Rent", "Utilities", "Insurance",
  "COGS", "Food & Beverage Cost", "Inventory & Merchandise",
  "Variable Costs", "Marketing & Advertising", "Professional Services",
  "Technology & Software", "Office & Supplies", "Travel & Entertainment",
  "Bank & Financial Fees", "Taxes & Licenses", "Miscellaneous",
  "Donations Received", "Program Services", "Fundraising", "Administrative" // Non-profit
];

interface ReviewTransaction {
  id: string;
  transaction_date: string;
  description: string;
  amount: number;
  category: string;
  confidence: number;
  source_file?: string;
  anomaly?: {
    type: string;
    severity: string;
    reason: string;
    recommendation: string;
  };
  needs_review: boolean;
  validation_status: 'pending' | 'approved' | 'modified' | 'rejected';
}

interface ReviewStats {
  total: number;
  approved: number;
  needsReview: number;
  anomalies: number;
  lowConfidence: number;
}

export default function DataReviewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken, userId } = useAuth();
  
  // State
  const [transactions, setTransactions] = useState<ReviewTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'needs_review' | 'anomalies' | 'low_confidence'>('needs_review');
  const [currentPage, setCurrentPage] = useState(1);
  const [runningAnomalyDetection, setRunningAnomalyDetection] = useState(false);
  
  const ITEMS_PER_PAGE = 50;
  const reportId = searchParams.get('reportId');
  
  // Load transactions from localStorage or API
  useEffect(() => {
    loadTransactions();
  }, [reportId]);
  
  const loadTransactions = async () => {
    setLoading(true);
    try {
      // First try to load from wizard state in localStorage
      const wizardData = localStorage.getItem('wizardData');
      if (wizardData) {
        const parsed = JSON.parse(wizardData);
        const unifiedData = parsed.unifiedUploadData?.canonical_data;
        
        if (unifiedData?.periods) {
          const allTxns: ReviewTransaction[] = [];
          
          unifiedData.periods.forEach((period: any, pIdx: number) => {
            (period.transactions || []).forEach((txn: any, tIdx: number) => {
              allTxns.push({
                id: `${pIdx}-${tIdx}`,
                transaction_date: txn.transaction_date,
                description: txn.description,
                amount: txn.amount,
                category: txn.category,
                confidence: txn.classification?.confidence || 0,
                source_file: txn.source_file,
                needs_review: txn.needs_review !== false && (txn.classification?.confidence || 0) < 0.95,
                validation_status: txn.validation_status || 'pending',
                anomaly: txn.anomaly
              });
            });
          });
          
          setTransactions(allTxns);
          setLoading(false);
          return;
        }
      }

      // No wizard data in localStorage → nothing to review (empty state).
      // (Removed a dead fallback to GET /api/reports/{reportId}/transactions — that route
      // does not exist and this page is only ever reached without a ?reportId= param.)
      setLoading(false);
    } catch (err) {
      console.error('Failed to load transactions:', err);
      setError('Failed to load transactions');
      setLoading(false);
    }
  };
  
  // Calculate stats
  const stats: ReviewStats = useMemo(() => {
    const approved = transactions.filter(t => t.validation_status === 'approved' || t.validation_status === 'modified').length;
    const needsReview = transactions.filter(t => t.needs_review && t.validation_status === 'pending').length;
    const anomalies = transactions.filter(t => t.anomaly).length;
    const lowConfidence = transactions.filter(t => t.confidence < 0.70).length;
    
    return {
      total: transactions.length,
      approved,
      needsReview,
      anomalies,
      lowConfidence
    };
  }, [transactions]);
  
  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      switch (filterType) {
        case 'needs_review':
          return t.needs_review && t.validation_status === 'pending';
        case 'anomalies':
          return t.anomaly !== undefined;
        case 'low_confidence':
          return t.confidence < 0.70;
        default:
          return true;
      }
    });
  }, [transactions, filterType]);
  
  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  // Approve single transaction
  const approveSingle = useCallback((id: string) => {
    setTransactions(prev => prev.map(t => 
      t.id === id ? { ...t, validation_status: 'approved', needs_review: false } : t
    ));
  }, []);
  
  // Bulk approve
  const approveSelected = useCallback(() => {
    setTransactions(prev => prev.map(t => 
      selectedIds.has(t.id) ? { ...t, validation_status: 'approved', needs_review: false } : t
    ));
    setSelectedIds(new Set());
  }, [selectedIds]);
  
  // Approve all visible
  const approveAllVisible = useCallback(() => {
    const visibleIds = new Set(paginatedTransactions.map(t => t.id));
    setTransactions(prev => prev.map(t => 
      visibleIds.has(t.id) && t.needs_review ? { ...t, validation_status: 'approved', needs_review: false } : t
    ));
  }, [paginatedTransactions]);
  
  // Edit category
  const startEdit = (txn: ReviewTransaction) => {
    setEditingId(txn.id);
    setEditCategory(txn.category);
  };
  
  const saveEdit = async (id: string) => {
    const txn = transactions.find(t => t.id === id);
    if (!txn) return;
    
    // Record AI learning. The backend CategoryCorrection model requires user_id, so guard on an
    // authenticated user id (skip the record rather than send a request the backend will 422).
    if (txn.category !== editCategory && userId) {
      try {
        // Use centralized API utility
        await api.post('/api/v1/cleanup/learnings/record', {
          user_id: userId,
          description: txn.description,
          original_category: txn.category,
          corrected_category: editCategory,
          amount: txn.amount
        }, { showErrorToast: false });
        console.log('📚 AI Learning recorded');
      } catch (err) {
        console.error('Failed to record learning:', err);
      }
    }
    
    setTransactions(prev => prev.map(t => 
      t.id === id ? { 
        ...t, 
        category: editCategory, 
        validation_status: 'modified', 
        needs_review: false 
      } : t
    ));
    setEditingId(null);
  };
  
  // Run anomaly detection
  const runAnomalyDetection = async () => {
    setRunningAnomalyDetection(true);
    try {
      // Use centralized API utility
      const data = await api.post('/api/v1/anomalies/detect', {
        transactions: transactions.slice(0, 100).map(t => ({
          description: t.description,
          amount: t.amount,
          category: t.category,
          date: t.transaction_date
        }))
      });
      
      // Merge anomalies into transactions
      setTransactions(prev => prev.map((t, idx) => {
        const anomaly = data.anomalies?.find((a: any) => a.transaction_index === idx);
        return anomaly ? { ...t, anomaly, needs_review: true } : t;
      }));
    } catch (err) {
      console.error('Anomaly detection failed:', err);
    } finally {
      setRunningAnomalyDetection(false);
    }
  };
  
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };
  
  const getConfidenceColor = (confidence: number): React.CSSProperties => {
    if (confidence >= 0.85) return { background: 'transparent', color: 'var(--color-verified)' };
    if (confidence >= 0.70) return { background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' };
    return { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' };
  };

  const getSeverityColor = (severity: string): React.CSSProperties => {
    switch (severity) {
      case 'high': return { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
      case 'medium': return { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
      default: return { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' };
    }
  };
  
  // Progress percentage
  const progressPercent = stats.total > 0 
    ? Math.round((stats.approved / stats.total) * 100) 
    : 0;
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="cs-body p-4" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14 }}>
          {error}
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6" style={{ background: 'var(--color-bg-base)' }}>
        {/* Header */}
        <div className="mb-6">
          <h1 className="cs-display">Data Review</h1>
          <p className="cs-meta">Review and approve transactions for your claim</p>
        </div>

        {/* Progress Bar */}
        <div className="p-4 mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
          <div className="flex items-center justify-between mb-2">
            <span className="cs-label">Review Progress</span>
            <span className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>{progressPercent}% Complete</span>
          </div>
          <div className="w-full rounded-full h-3" style={{ background: 'var(--color-bg-elevated)' }}>
            <div
              className="h-3 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%`, background: 'var(--color-brand)' }}
            />
          </div>
          <div className="flex justify-between mt-2">
            <span className="cs-meta">{stats.approved} approved</span>
            <span className="cs-meta">{stats.needsReview} need review</span>
            <span className="cs-meta">{stats.total} total</span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'transparent' }}>
                <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              </div>
              <div>
                <p className="cs-figure-lg">{stats.approved}</p>
                <p className="cs-meta">Approved</p>
              </div>
            </div>
          </div>

          <div className="p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                <Clock className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div>
                <p className="cs-figure-lg">{stats.needsReview}</p>
                <p className="cs-meta">Need Review</p>
              </div>
            </div>
          </div>

          <div className="p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                <AlertOctagon className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div>
                <p className="cs-figure-lg">{stats.anomalies}</p>
                <p className="cs-meta">Anomalies</p>
              </div>
            </div>
          </div>

          <div className="p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div>
                <p className="cs-figure-lg">{stats.lowConfidence}</p>
                <p className="cs-meta">Low Confidence</p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Quality Report Panel */}
        {reportId && (
          <div className="mb-6">
            <DataQualityReportPanel claimId={reportId} />
          </div>
        )}

        {/* Actions Bar */}
        <div className="p-4 mb-6 flex items-center justify-between" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
          <div className="flex items-center gap-4">
            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
              <select
                value={filterType}
                onChange={(e) => { setFilterType(e.target.value as any); setCurrentPage(1); }}
                className="cs-body rounded-lg px-3 py-2"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              >
                <option value="all">All Transactions ({transactions.length})</option>
                <option value="needs_review">Needs Review ({stats.needsReview})</option>
                <option value="anomalies">Anomalies ({stats.anomalies})</option>
                <option value="low_confidence">Low Confidence ({stats.lowConfidence})</option>
              </select>
            </div>

            {/* Selection info */}
            {selectedIds.size > 0 && (
              <span className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
                {selectedIds.size} selected
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Anomaly detection button */}
            <button
              onClick={runAnomalyDetection}
              disabled={runningAnomalyDetection}
              className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition disabled:opacity-50"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              {runningAnomalyDetection ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Run AI Anomaly Check
            </button>

            {/* Bulk approve */}
            {selectedIds.size > 0 ? (
              <button
                onClick={approveSelected}
                className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                <Check className="w-4 h-4" />
                Approve Selected ({selectedIds.size})
              </button>
            ) : (
              <button
                onClick={approveAllVisible}
                className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                <Check className="w-4 h-4" />
                Approve All Visible
              </button>
            )}
          </div>
        </div>
        
        {/* Transactions Table */}
        <div className="overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
          <table className="w-full">
            <thead style={{ background: 'var(--color-bg-base)', borderBottom: '1px solid var(--color-border-sub)' }}>
              <tr>
                <th className="cs-label text-left py-3 px-4 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === paginatedTransactions.filter(t => t.needs_review).length && selectedIds.size > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(new Set(paginatedTransactions.filter(t => t.needs_review).map(t => t.id)));
                      } else {
                        setSelectedIds(new Set());
                      }
                    }}
                    className="w-4 h-4 rounded"
                  />
                </th>
                <th className="cs-label text-left py-3 px-4">Date</th>
                <th className="cs-label text-left py-3 px-4">Description</th>
                <th className="cs-label text-right py-3 px-4">Amount</th>
                <th className="cs-label text-left py-3 px-4">Category</th>
                <th className="cs-label text-center py-3 px-4">Confidence</th>
                <th className="cs-label text-center py-3 px-4">Status</th>
                <th className="cs-label text-center py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.map((txn) => (
                <tr
                  key={txn.id}
                  style={{
                    borderBottom: '1px solid var(--color-border-faint)',
                    background: txn.anomaly
                      ? 'var(--color-needs-human-tint)'
                      : txn.needs_review
                        ? 'var(--color-bg-elevated)'
                        : 'transparent',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-base)'; }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = txn.anomaly
                      ? 'var(--color-needs-human-tint)'
                      : txn.needs_review
                        ? 'var(--color-bg-elevated)'
                        : 'transparent';
                  }}
                >
                  {/* Checkbox */}
                  <td className="py-3 px-4">
                    {txn.needs_review && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(txn.id)}
                        onChange={() => {
                          const next = new Set(selectedIds);
                          if (next.has(txn.id)) next.delete(txn.id);
                          else next.add(txn.id);
                          setSelectedIds(next);
                        }}
                        className="w-4 h-4 rounded"
                      />
                    )}
                  </td>

                  {/* Date */}
                  <td className="cs-body py-3 px-4 whitespace-nowrap" style={{ color: 'var(--color-text-primary)' }}>
                    {formatDate(txn.transaction_date)}
                  </td>

                  {/* Description + Anomaly */}
                  <td className="py-3 px-4 max-w-xs">
                    <div className="cs-body truncate" title={txn.description}>
                      {txn.description}
                    </div>
                    {txn.anomaly && (
                      <div className="cs-meta mt-1 px-2 py-1 rounded flex items-center gap-1" style={{ border: '1px solid', ...getSeverityColor(txn.anomaly.severity) }}>
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" aria-hidden="true" /> {txn.anomaly.reason}
                      </div>
                    )}
                  </td>

                  {/* Amount */}
                  <td className="cs-figure py-3 px-4 text-right whitespace-nowrap">
                    <span style={{ color: txn.amount >= 0 ? 'var(--color-text-secondary)' : 'var(--color-text-muted)' }}>
                      {formatCurrency(txn.amount, { cents: true })}
                    </span>
                  </td>

                  {/* Category */}
                  <td className="py-3 px-4">
                    {editingId === txn.id ? (
                      <select
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        className="cs-body w-full px-2 py-1 rounded"
                        style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                        autoFocus
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    ) : (
                      <button
                        onClick={() => startEdit(txn)}
                        className="cs-chip px-2 py-1 rounded transition"
                        style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                      >
                        {txn.category}
                      </button>
                    )}
                  </td>

                  {/* Confidence */}
                  <td className="py-3 px-4 text-center">
                    <span className="cs-chip px-2 py-1 rounded" style={getConfidenceColor(txn.confidence)}>
                      {Math.round(txn.confidence * 100)}%
                    </span>
                  </td>

                  {/* Status */}
                  <td className="py-3 px-4 text-center">
                    {txn.validation_status === 'approved' || txn.validation_status === 'modified' ? (
                      <span className="cs-chip inline-flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'transparent', color: 'var(--color-verified)' }}>
                        <CheckCircle2 className="w-3 h-3" />
                        {txn.validation_status === 'modified' ? 'Modified' : 'Approved'}
                      </span>
                    ) : txn.needs_review ? (
                      <span className="cs-chip inline-flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                        <AlertTriangle className="w-3 h-3" />
                        Review
                      </span>
                    ) : (
                      <span className="cs-meta" style={{ color: 'var(--color-text-ghost)' }}>—</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="py-3 px-4 text-center">
                    {editingId === txn.id ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => saveEdit(txn.id)}
                          className="p-1 rounded"
                          style={{ color: 'var(--color-verified)' }}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1 rounded"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : txn.needs_review ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => approveSingle(txn.id)}
                          className="p-1.5 rounded"
                          style={{ color: 'var(--color-verified)' }}
                          title="Approve"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => startEdit(txn)}
                          className="p-1.5 rounded"
                          style={{ color: 'var(--color-brand)' }}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEdit(txn)}
                        className="p-1 rounded"
                        style={{ color: 'var(--color-text-dim)' }}
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Empty state */}
          {paginatedTransactions.length === 0 && (
            <div className="text-center py-12">
              {filterType === 'needs_review' ? (
                <>
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-verified)' }} />
                  <p className="cs-h3">All transactions reviewed!</p>
                  <p className="cs-meta">Great job - no more items need attention.</p>
                </>
              ) : (
                <p className="cs-meta">No transactions match the current filter.</p>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid var(--color-border-sub)', background: 'var(--color-bg-base)' }}>
              <span className="cs-meta">
                Page {currentPage} of {totalPages} • {filteredTransactions.length} transactions
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded disabled:opacity-50"
                  style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded disabled:opacity-50"
                  style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Actions */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={() => navigate('/wizard-v2')}
            className="cs-body px-4 py-2"
            style={{ color: 'var(--color-text-muted)' }}
          >
            ← Back to Wizard
          </button>

          {progressPercent >= 100 && (
            <button
              onClick={() => navigate('/wizard-v2?step=4')}
              className="cs-body px-6 py-2 rounded-lg"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Continue to Date Selection →
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
